import { HdBodygraphRenderStateFactory } from './hd-bodygraph-render-state.factory';
import { getHdBodygraphChannel } from './hd-bodygraph-metadata';
import { deriveEnergyType } from './basic-properties/hd-chart-properties.util';
import { HdBodygraphConfig } from './hd-bodygraph.models';

/**
 * Kanaaltype volgens de "lege chart met enkel dit kanaal"-regel (spec
 * `2026-07-06-hd-moon-transit-chart-design.md` §3.3): het energie-type van een
 * chart met alléén de twee poorten van het kanaal bepaalt het type —
 * manifestor of pure MG → `manifested`, generator → `generated`, projector →
 * `projected`. Een statische eigenschap van het kanaal, onafhankelijk van de
 * geladen chart.
 */
export type HdChannelManifestationType = 'projected' | 'generated' | 'manifested';

const manifestationTypeCache = new Map<string, HdChannelManifestationType | null>();

/**
 * Gememoïzeerd kanaaltype voor een canonieke kanaal-key uit
 * `HD_BODYGRAPH_CHANNELS` (bv. `'30-41'`). Hergebruikt `deriveEnergyType` op
 * een synthetische één-kanaal-render-state — géén eigen typelogica. Reflector
 * is onbereikbaar: een kanaal definieert altijd zijn twee centers. Geeft
 * `null` voor een onbekende key.
 */
export function getHdChannelManifestationType(channelKey: string): HdChannelManifestationType | null {
  const cached = manifestationTypeCache.get(channelKey);
  if (cached !== undefined) {
    return cached;
  }
  const channel = getHdBodygraphChannel(channelKey);
  const result = channel == null ? null : computeManifestationType(channel.gates);
  manifestationTypeCache.set(channelKey, result);
  return result;
}

function computeManifestationType(gates: readonly [number, number]): HdChannelManifestationType {
  const configuration: HdBodygraphConfig = {
    schemaVersion: 1,
    activations: gates.map(gate => ({
      id: `manifestation-type-${gate}`,
      gate,
      side: 'personality',
      sourceType: 'manual',
      sourceKey: 'manifestation-type',
    })),
  };
  const renderState = new HdBodygraphRenderStateFactory().create(configuration);
  const type = deriveEnergyType(renderState);
  if (type.base === 'manifestor' || (type.base === 'generator' && type.modifier === 'manifesting')) {
    return 'manifested';
  }
  return type.base === 'generator' ? 'generated' : 'projected';
}
